/**
 * ComparePage — competitive comparison pages (iter 176).
 *
 * Route: /compare/caraloom-vs-{competitor}
 *
 * These pages target high-intent commercial queries ("caraloom vs
 * careport", "careport alternative", "aidin vs caraloom") — where the
 * searcher already knows they need discharge-planning software and is
 * doing final vendor selection.
 *
 * Editorial rules (STRICT):
 *   - Every claim about a competitor must be independently verifiable
 *     from the competitor's public site or public regulatory/press
 *     coverage. No pricing figures unless publicly listed.
 *   - Use "we understand", "based on public documentation", and dated
 *     citations when discussing competitor features.
 *   - No fabricated screenshots, ratings, or capabilities.
 *   - Where CaraLoom clearly leads, we say so and back it up.
 *   - Where the competitor leads (e.g., install base), we acknowledge it.
 *
 * Testids: compare-{slug}, compare-hero, compare-table, compare-cta
 */
import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Check, X, Info, ShieldCheck } from 'lucide-react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { Seo } from '../components/seo/Seo';
import {
  organizationSchema, websiteSchema, breadcrumbSchema, faqSchema,
} from '../lib/seoSchemas';

// ─────────────────────────────────────────────────────────────
// Comparison configs
// ─────────────────────────────────────────────────────────────

const COMPARES = {
  'caraloom-vs-careport': {
    slug: 'caraloom-vs-careport',
    competitor: 'CarePort',
    metaTitle: 'CaraLoom vs CarePort — Discharge Planning Software Comparison | CaraLoom',
    metaDescription:
      'CaraLoom vs CarePort — a factual comparison for hospital case managers evaluating discharge planning and post-acute referral software. Feature-by-feature, honest, updated 2026.',
    keywords: [
      'caraloom vs careport',
      'careport alternative',
      'careport competitor',
      'discharge planning software comparison',
      'post acute referral platform comparison',
    ],
    lastUpdated: '2026-08-16',
    tagline:
      'CarePort has the biggest install base in the category. CaraLoom is API-first, coverage-native, and built around the case manager\u2019s exception queue. Here\u2019s the honest breakdown.',
    disclaimer:
      'This comparison is based on public information from CarePort\u2019s website and industry coverage as of the "Last updated" date. If any claim is out of date, please contact us and we\u2019ll correct it.',
    // Rows: [category, {caraloom, competitor}]
    table: [
      { category: 'Category positioning',
        caraloom: 'Post-acute care & discharge planning platform',
        competitor: 'Care coordination & post-acute network management (owned by WellSky since 2020)',
      },
      { category: 'Primary UX orientation',
        caraloom: 'Case-manager exception queue with one prioritized next action per case',
        competitor: 'Traditional workflow queues; comprehensive across the pre-acute-to-post-acute continuum',
      },
      { category: 'Coverage & authorization inside the loop',
        caraloom: 'In-loop eligibility + authorization before signature; loop pauses on hold',
        competitor: 'Coverage support present; typically routed to utilization review as a parallel workflow',
      },
      { category: 'Patient choice documentation',
        caraloom: 'Ranked qualifying list + time-stamped choice capture, CMS-ready by default',
        competitor: 'Patient-choice documentation supported',
      },
      { category: 'HIE record retrieval',
        caraloom: 'Native — CommonWell + Carequality via Metriport, patient-consent-gated',
        competitor: 'Available through the WellSky suite via separate integrations',
      },
      { category: 'API integration surface',
        caraloom: 'API-first — FHIR + HL7 + secure eFax ingestion',
        competitor: 'Enterprise integrations available; EHR-vendor-specific timelines',
      },
      { category: 'Deployment model',
        caraloom: 'Cloud-native, multi-tenant per hospital with system-wide rollups',
        competitor: 'Cloud-hosted; enterprise sales-led deployment',
      },
      { category: 'Data model transparency',
        caraloom: 'Provider records are verified from CMS Care Compare + provider claims; source cited on every profile',
        competitor: 'Extensive proprietary network data (~130,000+ post-acute providers, per public materials)',
      },
      { category: 'Pricing',
        caraloom: 'Custom; no per-referral fees on the discharge-planning side',
        competitor: 'Custom; not publicly listed',
      },
      { category: 'Install base',
        caraloom: 'Early-stage; growing case-management deployments',
        competitor: 'Widely deployed across acute-care hospitals in the U.S.',
      },
    ],
    strengths: {
      caraloom: [
        'Exception queue that shows what\u2019s stuck instead of what\u2019s new',
        'Payer coverage runs inside the loop, not after the fact',
        'Native HIE record retrieval via Metriport',
        'Provider directory is verified from public CMS data — no fabricated capacity or acceptance',
        'API-first with FHIR + HL7 and a public developer surface',
        'HIPAA-grade audit trail on every action, no bolt-on',
      ],
      competitor: [
        'Longest history in the category and the largest installed base',
        'Very broad post-acute network coverage via WellSky',
        'Well-known to health-system IT and CFO buyers',
        'Deep case-management workflow depth accumulated over years',
      ],
    },
    faqs: [
      { q: 'Is CaraLoom a direct replacement for CarePort?',
        a: 'For the discharge-planning and post-acute referral surface — yes, CaraLoom is designed to replace the workflow. For hospitals already deeply integrated with WellSky\u2019s broader suite of tools, we recommend evaluating both against your specific workflow bottlenecks.' },
      { q: 'When does it make sense to choose CarePort over CaraLoom?',
        a: 'CarePort is a strong choice when a hospital wants a single vendor across pre-acute + post-acute + payer + LTPAC and is already inside the WellSky ecosystem. CaraLoom is the better choice when the priority is case-manager throughput, coverage-native workflow, API-first integration, and a modern, transparent provider directory.' },
      { q: 'Can we migrate historical referral data from CarePort to CaraLoom?',
        a: 'Yes. CaraLoom supports referral history import via CSV or API. The typical migration takes 1\u20132 weeks alongside the standard EHR integration.' },
      { q: 'How is CaraLoom\u2019s pricing structured?',
        a: 'Custom by health-system size and hospital count. Unlike some competitors, CaraLoom does not charge per referral or per accepted placement — the platform is priced as a subscription plus optional integration modules.' },
    ],
  },
  'caraloom-vs-aidin': {
    slug: 'caraloom-vs-aidin',
    competitor: 'Aidin',
    metaTitle: 'CaraLoom vs Aidin — Post-Acute Referral Platform Comparison | CaraLoom',
    metaDescription:
      'CaraLoom vs Aidin — factual comparison for hospital case managers evaluating post-acute referral and patient-choice software. Feature-by-feature, updated 2026.',
    keywords: [
      'caraloom vs aidin',
      'aidin alternative',
      'aidin competitor',
      'patient choice software comparison',
      'post acute referral platform comparison',
    ],
    lastUpdated: '2026-08-16',
    tagline:
      'Aidin pioneered CMS-compliant patient-choice documentation at scale. CaraLoom brings that surface into a full discharge-planning loop with coverage, orders, and outcome tracking. Here\u2019s the honest breakdown.',
    disclaimer:
      'This comparison is based on public information from Aidin\u2019s website and industry coverage as of the "Last updated" date. If any claim is out of date, please contact us and we\u2019ll correct it.',
    table: [
      { category: 'Category positioning',
        caraloom: 'Post-acute care & discharge planning platform',
        competitor: 'Patient-choice + post-acute referral platform',
      },
      { category: 'Patient choice documentation',
        caraloom: 'Ranked qualifying list + time-stamped choice capture, CMS-ready',
        competitor: 'Patient choice is a core, well-established feature',
      },
      { category: 'Coverage & authorization inside the loop',
        caraloom: 'In-loop eligibility + authorization before signature',
        competitor: 'Coverage supported alongside the referral surface',
      },
      { category: 'Digital order set (F2F, 485, NOA, consent)',
        caraloom: 'Digital signatures on the standard order set inside the same thread',
        competitor: 'Order support present; scope varies by module',
      },
      { category: 'HIE record retrieval',
        caraloom: 'Native — CommonWell + Carequality via Metriport, consent-gated',
        competitor: 'Not a headline feature in public materials',
      },
      { category: 'Length-of-stay analytics',
        caraloom: 'Native dashboards on time-to-placement, decline reasons, avoidable days',
        competitor: 'Analytics available on referral throughput + response signal',
      },
      { category: 'API-first integration',
        caraloom: 'API-first — FHIR + HL7 + secure eFax ingestion',
        competitor: 'EHR integrations available; enterprise sales-led',
      },
      { category: 'Provider directory',
        caraloom: 'Verified from public CMS Care Compare + provider claims; source cited',
        competitor: 'Network data collected via referral history + provider partnerships',
      },
      { category: 'Pricing',
        caraloom: 'Custom; no per-referral fees on the discharge-planning side',
        competitor: 'Custom; not publicly listed',
      },
    ],
    strengths: {
      caraloom: [
        'Full discharge loop — search, patient choice, coverage, orders, placement, tracking — in one thread',
        'HIE record retrieval integrated natively',
        'Provider directory is verified from public CMS data, transparent about sources',
        'API-first with FHIR + HL7 and a public developer surface',
        'Length-of-stay and avoidable-day analytics native to the platform',
      ],
      competitor: [
        'Established leader in patient-choice documentation at scale',
        'Extensive experience with CMS discharge-planning compliance',
        'Strong presence in specific hospital markets',
      ],
    },
    faqs: [
      { q: 'How does CaraLoom compare on patient choice specifically?',
        a: 'Patient choice is a first-class feature on both platforms. CaraLoom generates the ranked qualifying list, presents it to the patient, and captures the choice with time-stamp and user in the audit log. Aidin has a well-established patient-choice product with a longer track record. If patient choice is the only requirement, both are appropriate.' },
      { q: 'What does CaraLoom offer that Aidin doesn\u2019t?',
        a: 'The full discharge loop in one thread: automated matching, in-loop coverage and authorization, digital signature on the standard order set (F2F, 485, NOA, consent), native HIE record retrieval, and length-of-stay analytics. CaraLoom also ships an API-first surface with FHIR and HL7.' },
      { q: 'Can we run both platforms during a transition period?',
        a: 'Yes. Hospitals commonly run parallel deployments during a 30\u201390 day migration window. CaraLoom\u2019s API surface makes it easy to sync referral state during that window.' },
    ],
  },
};

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

function StrengthsList({ items, testId }) {
  return (
    <ul className="space-y-2" data-testid={testId}>
      {items.map((s, i) => (
        <li key={i} className="flex items-start gap-2 text-cn-text-muted leading-relaxed">
          <Check className="h-4 w-4 text-cn-primary shrink-0 mt-1" /> {s}
        </li>
      ))}
    </ul>
  );
}

export default function ComparePage() {
  const { slug } = useParams();
  const c = COMPARES[slug];
  if (!c) return <Navigate to="/discharge-planning-software" replace />;

  const path = `/compare/${slug}`;
  const trail = [
    { name: 'Home', path: '/' },
    { name: 'Compare', path: '/compare' },
    { name: `CaraLoom vs ${c.competitor}`, path },
  ];
  const schemas = [
    organizationSchema(),
    websiteSchema(),
    breadcrumbSchema(trail),
  ];
  if (c.faqs?.length) schemas.push(faqSchema(c.faqs));
  const lastUpdatedStr = new Date(c.lastUpdated).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <div className="min-h-screen bg-cn-bg" data-testid={`compare-${slug}`}>
      <Seo
        title={c.metaTitle}
        description={c.metaDescription}
        path={path}
        keywords={c.keywords}
        schema={schemas}
      />
      <PublicNavbar />

      <section className="cn-container pt-8 pb-4" data-testid="compare-hero">
        <nav aria-label="Breadcrumb" className="text-xs text-cn-text-muted mb-6">
          <Link to="/" className="hover:text-cn-primary">Home</Link>
          <span className="mx-2">/</span>
          <span className="text-cn-text/70">Compare</span>
          <span className="mx-2">/</span>
          <span className="text-cn-text/70">CaraLoom vs {c.competitor}</span>
        </nav>
        <div className="inline-flex items-center gap-1.5 rounded-full bg-cn-primary/10 border border-cn-primary/25 px-3 py-1 text-xs font-semibold text-cn-primary tracking-wide uppercase mb-5">
          <Info className="h-3 w-3" /> Vendor Comparison
        </div>
        <h1 className="cn-display text-4xl sm:text-5xl lg:text-6xl text-cn-text tracking-tight leading-[1.05] max-w-3xl">
          CaraLoom vs {c.competitor}
        </h1>
        <p className="mt-6 text-lg md:text-xl text-cn-text-muted leading-relaxed max-w-2xl">
          {c.tagline}
        </p>
        <p className="mt-4 text-xs text-cn-text-muted italic">
          Last updated {lastUpdatedStr}. {c.disclaimer}
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/contact?type=demo"
            className="cn-btn cn-btn-primary text-sm"
            data-testid="compare-cta-demo"
          >
            Request a CaraLoom demo <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/discharge-planning-software"
            className="cn-btn cn-btn-ghost text-sm"
          >
            See the platform <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Feature-by-feature table */}
      <section className="cn-container pb-12" data-testid="compare-table">
        <h2 className="cn-display text-2xl md:text-3xl text-cn-text mt-10 mb-6">Feature by feature</h2>
        <div className="rounded-2xl border border-cn-ink/10 bg-white/60 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-cn-text-muted border-b border-cn-ink/10">
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium text-cn-primary">CaraLoom</th>
                <th className="px-4 py-3 font-medium">{c.competitor}</th>
              </tr>
            </thead>
            <tbody>
              {c.table.map((row, i) => (
                <tr key={i} className="border-b border-cn-ink/5 last:border-0 align-top">
                  <td className="px-4 py-4 font-semibold text-cn-text">{row.category}</td>
                  <td className="px-4 py-4 text-cn-text-muted leading-relaxed">{row.caraloom}</td>
                  <td className="px-4 py-4 text-cn-text-muted leading-relaxed">{row.competitor}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Strengths side-by-side */}
      <section className="cn-container pb-12">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-cn-primary/25 bg-cn-primary/5 p-6">
            <h3 className="font-semibold text-cn-text mb-4 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-cn-primary" /> Where CaraLoom leads
            </h3>
            <StrengthsList items={c.strengths.caraloom} testId="compare-caraloom-strengths" />
          </div>
          <div className="rounded-2xl border border-cn-ink/10 bg-white/60 p-6">
            <h3 className="font-semibold text-cn-text mb-4">Where {c.competitor} leads</h3>
            <StrengthsList items={c.strengths.competitor} testId="compare-competitor-strengths" />
          </div>
        </div>
      </section>

      {/* FAQs */}
      {c.faqs?.length > 0 && (
        <section className="cn-container pb-16">
          <h2 className="cn-display text-2xl md:text-3xl text-cn-text mt-6 mb-6">Common questions</h2>
          <div className="space-y-4">
            {c.faqs.map((f, i) => (
              <div key={i} className="rounded-2xl border border-cn-ink/10 bg-white/60 p-5" data-testid={`compare-faq-${i}`}>
                <h4 className="font-semibold text-cn-text mb-2">{f.q}</h4>
                <p className="text-cn-text-muted leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="cn-container pb-24 text-center">
        <div className="rounded-3xl border border-cn-primary/25 bg-cn-primary/5 p-10">
          <ShieldCheck className="h-10 w-10 mx-auto text-cn-primary mb-4" />
          <h2 className="cn-display text-2xl md:text-3xl text-cn-text mb-3">
            See CaraLoom on your discharge mix
          </h2>
          <p className="text-cn-text-muted max-w-2xl mx-auto mb-6">
            30-minute demo with your data model in mind. No PHI required. Bring your case-management leaders — the workflow speaks for itself.
          </p>
          <Link to="/contact?type=demo" className="cn-btn cn-btn-primary text-sm">
            Request a demo <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
